import Alert from '../../../../components/alert/Alert';
import classnames from 'classnames';
import { useQuery } from 'react-query';
import { QUERIES_IDS } from '../../../../util/vars';
import { repository } from '@whizz/api-client';
import { renderMandrillTemplate } from '../../../../helper/handlebars';
import type { DateTime } from 'luxon';
import { useI18n } from '@whizz/react-i18n';

const WEEKLY_LEADERS_EMAIL_TEMPLATE = 'weekly-leaders-en-gb';

export default function EmailPreview({ previewData }: Props) {
  const { translate: t } = useI18n();

  const getMailchimpRenderedTemplateQuery = useQuery({
    queryKey: [ QUERIES_IDS.ADMIN.GET_MAILCHIMP_RENDERED_TEMPLATE, { template: WEEKLY_LEADERS_EMAIL_TEMPLATE } ],
    queryFn: () => repository.AdminRepository.getMailchimpRenderedTemplate(WEEKLY_LEADERS_EMAIL_TEMPLATE),
  });

  const getWeeklyLeadersPreviewDynamicDataQuery = useQuery({
    queryKey: [
      QUERIES_IDS.ADMIN.GET_SCHOOL_WEEKLY_LEADERS_EMAIL_PREVIEW_DYNAMIC_DATA,
      {
        school: previewData.school!.id,
        startDate: previewData.startDate,
        endDate: previewData.endDate,
      },
    ],
    queryFn: () => repository.AdminRepository.getSchoolWeeklyLeadersEmailPreviewDynamicData(previewData.school!.id, previewData.startDate!, previewData.endDate!),
    enabled: null !== previewData.startDate && null !== previewData.endDate,
  });

  return (
    <div className={'flex flex-col gap-5'}>
      <h2 className={'text-xl font-bold'}>
        {t('pages.ADMIN.PREVIEW.TITLE')}
      </h2>

      {!previewData.isEnabled && (
        <Alert show type={'warning'}>
          {t('pages.ADMIN.PREVIEW.DISABLED')}
        </Alert>
      )}

      <div className={classnames({ 'opacity-50': !previewData.isEnabled })}>
        {(getMailchimpRenderedTemplateQuery.isSuccess) && (
          <div
            dangerouslySetInnerHTML={{
              __html: renderMandrillTemplate(
                getMailchimpRenderedTemplateQuery.data.data,
                {
                  school: {
                    name: previewData.school!.name,
                  },
                  startDate: {
                    weekday: previewData.startDate?.toFormat('cccc'),
                    dayMonth: previewData.startDate?.toFormat('dd MMMM'),
                  },
                  endDate: {
                    weekday: previewData.endDate?.toFormat('cccc'),
                    dayMonth: previewData.endDate?.toFormat('dd MMMM'),
                  },
                  teachers: previewData.teachers.join(', '),
                  standardGreeting: previewData.standardGreeting,
                  message: previewData.message?.split('\n').join('<br />'),
                  reminderMessage: previewData.reminderMessage?.split('\n').join('<br />'),
                  metricsComponents: previewData.metricsComponents.map((metricsComponent) => ({
                    type: metricsComponent,
                    value: getWeeklyLeadersPreviewDynamicDataQuery.data?.data.stats.metricsComponents[metricsComponent].value ?? 0,
                    previousValue: getWeeklyLeadersPreviewDynamicDataQuery.data?.data.stats.metricsComponents[metricsComponent].previousValue ?? 0,
                  })),
                  classes: getWeeklyLeadersPreviewDynamicDataQuery.data?.data.stats.classes ?? [],
                },
              ),
            }}
          />
        )}
      </div>
    </div>
  );
}


interface Props {
  previewData: PreviewData;
}

export interface PreviewData {
  isEnabled: boolean;
  school: {
    id: number;
    name: string;
  } | null;
  startDate: DateTime | null;
  endDate: DateTime | null;
  teachers: string[];
  standardGreeting: boolean;
  message: string | null;
  reminderMessage: string | null;
  metricsComponents: ('usage' | 'progressions')[];
  studentsData: {
    fullName: string;
    assessed: number;
    unAssessed: number;
    usage: number;
    progressions: number;
    extraProgressions: number;
  }[];
}
